/**
 * MasteryBox — Mastery W3 visual component.
 *
 * Renders the student's Leitner 5-box distribution into a compact set of
 * boxes (1→5) plus four mastery level chips (new / practiced / proficient /
 * mastered) summarising how many KPs sit at each level.
 *
 * 屬「學習端 academy/home navigation 層」 (boss memory feedback_learn_pro_mode):
 *   pill / box visual + chip allowed here. NEVER inject this into the quiz
 *   answer screen (proMode strips all decoration there).
 *
 * Real schema reference (src/shared/types.ts L415-427):
 *   StudentKP { box: LeitnerBox; totalAttempts; correctCount; lastReviewedISO; ... }
 *
 * NOTE: getMasteryLevel needs correctRate + attempts + box; we derive
 *   correctRate = correctCount / totalAttempts (matches src/shared/student-kp
 *   masteryPercent convention).
 *
 * INVARIANTS:
 *   - pure DOM render (no game/ imports).
 *   - idempotent: clears prior [data-testid="mastery-box"] before redraw.
 */

import type { LeitnerBox, StudentKP } from '../../shared/types';
import { getMasteryLevel, type MasteryLevel } from '../mastery';

/** Display cap to avoid 3-digit overflow inside a small box badge. */
export const MASTERY_BOX_COUNT_CAP = 99;

/** Box → color ladder (灰/橘/黃/綠/金) per task spec. */
const BOX_COLORS: Record<LeitnerBox, { bg: string; fg: string; border: string }> = {
  1: { bg: '#ECEFF1', fg: '#37474F', border: '#B0BEC5' }, // 灰
  2: { bg: '#FFE0B2', fg: '#5D4037', border: '#FB8C00' }, // 橘
  3: { bg: '#FFF59D', fg: '#5D4037', border: '#FBC02D' }, // 黃
  4: { bg: '#C8E6C9', fg: '#1B5E20', border: '#43A047' }, // 綠
  5: { bg: '#FFD54F', fg: '#3E2723', border: '#FF8F00' }, // 金
};

/** Box labels (kid-readable, no jargon, no exam shaming). */
const BOX_LABELS: Record<LeitnerBox, string> = {
  1: '今日',
  2: '3 天',
  3: '一週',
  4: '兩週',
  5: '一個月',
};

/** Mastery level → display name (zh-TW, professional tone, no戲謔). */
const LEVEL_LABELS: Record<MasteryLevel, string> = {
  new: '尚未開始',
  practiced: '已練習',
  proficient: '熟練',
  mastered: '精通',
};

/** Level → color chip palette. */
const LEVEL_COLORS: Record<MasteryLevel, { bg: string; fg: string }> = {
  new: { bg: '#ECEFF1', fg: '#607D8B' },
  practiced: { bg: '#FFE0B2', fg: '#5D4037' },
  proficient: { bg: '#C8E6C9', fg: '#1B5E20' },
  mastered: { bg: '#FFD54F', fg: '#3E2723' },
};

const ALL_BOXES: LeitnerBox[] = [1, 2, 3, 4, 5];
const ALL_LEVELS: MasteryLevel[] = ['new', 'practiced', 'proficient', 'mastered'];

function safeCorrectRate(kp: StudentKP): number {
  if (kp.totalAttempts <= 0) return 0;
  return kp.correctCount / kp.totalAttempts;
}

/**
 * Bucket by box. Throws on negative `totalAttempts` / `correctCount` because
 * those represent corrupt save data we'd rather flag loudly.
 */
function groupByBox(kps: ReadonlyArray<StudentKP>): Record<LeitnerBox, number> {
  const counts: Record<LeitnerBox, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  for (const kp of kps) {
    if (kp.totalAttempts < 0 || kp.correctCount < 0) {
      throw new RangeError(
        `renderMasteryBox: KP ${kp.kpId} has negative counts (totalAttempts=${kp.totalAttempts}, correctCount=${kp.correctCount})`,
      );
    }
    const box = kp.box;
    if (!Number.isInteger(box) || box < 1 || box > 5) {
      // Skip clearly invalid box rather than crash an entire dashboard render.
      continue;
    }
    counts[box as LeitnerBox] += 1;
  }
  return counts;
}

/**
 * Compute mastery-level distribution. Each KP classified via getMasteryLevel.
 * Uses Date.now() injection via getMasteryLevel default (good enough — staleness
 * demote only matters for proficient/mastered; for the chip count we accept it).
 */
function groupByLevel(kps: ReadonlyArray<StudentKP>): Record<MasteryLevel, number> {
  const counts: Record<MasteryLevel, number> = {
    new: 0,
    practiced: 0,
    proficient: 0,
    mastered: 0,
  };
  for (const kp of kps) {
    const level = getMasteryLevel({
      box: kp.box,
      attempts: kp.totalAttempts,
      correctRate: safeCorrectRate(kp),
      lastAttemptAt: kp.lastReviewedISO ? Date.parse(kp.lastReviewedISO) : undefined,
    });
    counts[level] += 1;
  }
  return counts;
}

function displayCount(n: number): string {
  return n > MASTERY_BOX_COUNT_CAP ? `${MASTERY_BOX_COUNT_CAP}+` : String(n);
}

/**
 * Render the 5-box ladder + 4 mastery chips into `container`.
 *
 * @param container target DOM element. Null/undefined → no-op (safe).
 * @param kps       student KP list (undefined → treated as empty for empty-state).
 *
 * @throws RangeError if any KP has negative `totalAttempts` / `correctCount`.
 */
export function renderMasteryBox(
  container: HTMLElement,
  kps: StudentKP[] | undefined,
): void {
  if (!container) return;

  // Idempotent: drop prior render in this container.
  const prior = container.querySelector('[data-testid="mastery-box"]');
  if (prior) prior.remove();

  const list = kps ?? [];

  const root = document.createElement('div');
  root.className = 'mastery-box-root';
  root.setAttribute('data-testid', 'mastery-box');
  root.style.cssText = [
    'display:flex',
    'flex-direction:column',
    'gap:12px',
    'padding:14px',
    'background:var(--ef-paper-bright,#fefcf5)',
    'border:1px solid var(--ef-line,#e0d9c4)',
    'border-radius:12px',
    'color:var(--ef-ink-soft,#3a3a36)',
    'font-family:inherit',
  ].join(';');

  // Empty state — kid-friendly nudge.
  if (list.length === 0) {
    const empty = document.createElement('div');
    empty.setAttribute('data-testid', 'mastery-box-empty');
    empty.style.cssText = [
      'padding:20px',
      'text-align:center',
      'color:var(--ef-sub,#6b6b63)',
      'font-size:14px',
      'line-height:1.6',
    ].join(';');
    empty.textContent = '還沒開始,先做幾題吧';
    root.appendChild(empty);
    container.appendChild(root);
    return;
  }

  const boxCounts = groupByBox(list);
  const levelCounts = groupByLevel(list);

  // --- 5 boxes (asc by box id) ---
  const boxRow = document.createElement('div');
  boxRow.setAttribute('data-testid', 'mastery-box-row');
  boxRow.style.cssText = [
    'display:flex',
    'gap:8px',
    'justify-content:space-between',
    'flex-wrap:wrap',
  ].join(';');

  for (const box of ALL_BOXES) {
    const cell = document.createElement('div');
    const palette = BOX_COLORS[box];
    cell.setAttribute('data-testid', `mastery-box-cell-${box}`);
    cell.setAttribute('data-box', String(box));
    cell.style.cssText = [
      'flex:1 1 0',
      'min-width:54px',
      `background:${palette.bg}`,
      `color:${palette.fg}`,
      `border:1px solid ${palette.border}`,
      'border-radius:8px',
      'padding:8px 4px',
      'text-align:center',
      'font-variant-numeric:tabular-nums',
    ].join(';');

    const num = document.createElement('div');
    num.style.cssText = 'font-size:18px;font-weight:700;line-height:1.2;';
    num.textContent = displayCount(boxCounts[box]);

    const label = document.createElement('div');
    label.style.cssText = 'font-size:11px;opacity:0.85;margin-top:2px;';
    label.textContent = `${BOX_LABELS[box]} (${box})`;

    cell.append(num, label);
    boxRow.appendChild(cell);
  }
  root.appendChild(boxRow);

  // --- 4 mastery chips ---
  const chipRow = document.createElement('div');
  chipRow.setAttribute('data-testid', 'mastery-chip-row');
  chipRow.style.cssText = [
    'display:flex',
    'gap:6px',
    'flex-wrap:wrap',
  ].join(';');

  for (const level of ALL_LEVELS) {
    const chip = document.createElement('div');
    const palette = LEVEL_COLORS[level];
    chip.setAttribute('data-testid', `mastery-chip-${level}`);
    chip.setAttribute('data-level', level);
    chip.style.cssText = [
      'display:inline-flex',
      'align-items:center',
      'gap:4px',
      'padding:4px 10px',
      'border-radius:999px',
      `background:${palette.bg}`,
      `color:${palette.fg}`,
      'font-size:12px',
      'font-weight:600',
      'line-height:1',
    ].join(';');
    chip.textContent = `${LEVEL_LABELS[level]} ${displayCount(levelCounts[level])}`;
    chipRow.appendChild(chip);
  }
  root.appendChild(chipRow);

  container.appendChild(root);
}
